/**
 * Centralised query-key factory.
 *
 * Keys are hierarchical, so invalidating `shops.all` after a mutation clears
 * every shop list and detail at once without anyone having to remember the
 * exact filter object that was used to fetch them.
 */
export const queryKeys = {
  me: ['me'] as const,

  agents: {
    all: ['agents'] as const,
    list: (params: unknown) => ['agents', 'list', params] as const,
    detail: (id: number) => ['agents', 'detail', id] as const,
    suggest: (term: string) => ['agents', 'suggest', term] as const,
  },

  routes: {
    all: ['routes'] as const,
    list: (params: unknown) => ['routes', 'list', params] as const,
    detail: (id: number) => ['routes', 'detail', id] as const,
    shops: (id: number) => ['routes', 'shops', id] as const,
    suggest: (term: string) => ['routes', 'suggest', term] as const,
  },

  shops: {
    all: ['shops'] as const,
    list: (params: unknown) => ['shops', 'list', params] as const,
    detail: (id: number) => ['shops', 'detail', id] as const,
    invoices: (id: number, outstanding: boolean) =>
      ['shops', 'invoices', id, outstanding] as const,
    suggest: (term: string, mineOnly: boolean) => ['shops', 'suggest', term, mineOnly] as const,
  },

  banks: {
    all: ['banks'] as const,
    list: (includeInactive: boolean) => ['banks', 'list', includeInactive] as const,
  },

  invoices: {
    all: ['invoices'] as const,
    list: (params: unknown) => ['invoices', 'list', params] as const,
    detail: (id: number) => ['invoices', 'detail', id] as const,
  },

  collections: {
    all: ['collections'] as const,
    list: (params: unknown) => ['collections', 'list', params] as const,
    detail: (id: number) => ['collections', 'detail', id] as const,
  },

  reports: {
    all: ['reports'] as const,
    agentDaily: (agentId: number, date: string) => ['reports', 'agentDaily', agentId, date] as const,
    agentTotals: (date: string) => ['reports', 'agentTotals', date] as const,
    shopBreakdown: (date: string, agentId?: number) =>
      ['reports', 'shopBreakdown', date, agentId ?? null] as const,
    summary: (from: string, to: string, agentId?: number) =>
      ['reports', 'summary', from, to, agentId ?? null] as const,
    routeDaily: (date: string) => ['reports', 'routeDaily', date] as const,
    cheques: (from: string, to: string, agentId?: number) =>
      ['reports', 'cheques', from, to, agentId ?? null] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    main: (from: string, to: string) => ['dashboard', 'main', from, to] as const,
  },

  users: {
    all: ['users'] as const,
    list: (params: unknown) => ['users', 'list', params] as const,
  },

  auditLogs: {
    all: ['auditLogs'] as const,
    list: (params: unknown) => ['auditLogs', 'list', params] as const,
  },
} as const;
